import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, takeUntil, combineLatest, skip } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { CalendarEvent, EventStatus, EventType } from './models/calendar-event.interface';
import { CalendarService } from './services/calendar.service';

@Component({
  selector: 'app-new-project-calendar',
  templateUrl: './new-project-calendar.component.html',
  styleUrls: ['./new-project-calendar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class NewProjectCalendarComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private _loadingFromForm = false;

  @Input() propertyForm: FormGroup | null = null;

  // Estado del componente
  events: CalendarEvent[] = [];
  selectedEvent: CalendarEvent | null = null;
  isLoading = false;
  error: string | null = null;
  viewMode: 'timeline' | 'calendar' | 'list' = 'list';
  
  // Modal de eventos
  showEventModal = false;
  editingEvent: CalendarEvent | null = null;
  newEvent: Partial<CalendarEvent> = {
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    icon: '📅'
  };

  // Strings para los inputs de fecha
  startDateString = '';
  endDateString = '';

  // Opciones de iconos predefinidos
  iconOptions = [
    { icon: 'assets/images/icons/oportunidades/financing.png', label: 'Financiación' },
    { icon: 'assets/images/icons/oportunidades/purchase.png', label: 'Compra' },
    { icon: 'assets/images/icons/oportunidades/inicio_reforma.png', label: 'Inicio de Reforma' },
    { icon: 'assets/images/icons/oportunidades/informe_1.png', label: 'Informe 1' },
    { icon: 'assets/images/icons/oportunidades/informe_2.png', label: 'Informe 2' },
    { icon: 'assets/images/icons/oportunidades/informe_3.png', label: 'Informe 3' },
    { icon: 'assets/images/icons/oportunidades/fin_obra.png', label: 'Fin de obra' },
    { icon: 'assets/images/icons/oportunidades/venta.png', label: 'Venta' },
  ];

  // Drag & Drop
  isDragging = false;
  dragStartX = 0;
  dragStartProgress = 0;
  currentDraggingEvent: CalendarEvent | null = null;
  
  // Filtros
  statusFilter: EventStatus[] = [];
  typeFilter: EventType[] = [];
  
  // Estadísticas
  projectStats = {
    totalEvents: 0,
    completedEvents: 0,
    pendingEvents: 0,
    inProgressEvents: 0,
    completionPercentage: 0
  };

  // Opciones para filtros
  statusOptions: { value: EventStatus; label: string; color: string }[] = [
    { value: 'pending', label: 'Pendiente', color: '#6c757d' },
    { value: 'in-progress', label: 'En Progreso', color: '#ffc107' },
    { value: 'completed', label: 'Completado', color: '#28a745' },
    { value: 'cancelled', label: 'Cancelado', color: '#dc3545' }
  ];

  typeOptions: { value: EventType; label: string; icon: string }[] = [
    { value: 'financing', label: 'Financiación', icon: 'assets/images/icons/oportunidades/financing.png' },
    { value: 'purchase', label: 'Compra', icon: 'assets/images/icons/oportunidades/purchase.png' },
    { value: 'renovation', label: 'Reforma', icon: 'assets/images/icons/oportunidades/inicio_reforma.png' },
    { value: 'report', label: 'Informe', icon: 'assets/images/icons/oportunidades/informe_1.png' },
    { value: 'sale', label: 'Venta', icon: 'assets/images/icons/oportunidades/venta.png' }
  ];

  constructor(
    private calendarService: CalendarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.calendarService.resetState();
    this.subscribeToState();
    this.loadEventsFromPropertyForm();
    this.loadProjectStats();
    this.setupGlobalDragListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyForm'] && !changes['propertyForm'].firstChange) {
      this.loadEventsFromPropertyForm();
    }
  }

  /**
   * Carga eventos desde el FormControl de la propiedad
   */
  private loadEventsFromPropertyForm(): void {
    if (!this.propertyForm) {
      return;
    }

    const calendarEventsControl = this.propertyForm.get('calendarEvents');
    if (calendarEventsControl) {
      const events = calendarEventsControl.value || [];
      if (Array.isArray(events) && events.length > 0) {
        // Convertir strings de fecha a Date objects
        const parsedEvents = events.map((event: any) => {
          const iconMap: { [key: string]: string } = {
            '💰': 'assets/images/icons/oportunidades/financing.png',
            '🏠': 'assets/images/icons/oportunidades/purchase.png',
            '📝': 'assets/images/icons/oportunidades/purchase.png',
            '🔨': 'assets/images/icons/oportunidades/inicio_reforma.png',
            '🛠️': 'assets/images/icons/oportunidades/inicio_reforma.png',
            '🧱': 'assets/images/icons/oportunidades/inicio_reforma.png',
            '📋': 'assets/images/icons/oportunidades/informe_1.png',
            '👷': 'assets/images/icons/oportunidades/informe_1.png',
            '🚧': 'assets/images/icons/oportunidades/informe_2.png',
            '🎨': 'assets/images/icons/oportunidades/informe_3.png',
            '🏗️': 'assets/images/icons/oportunidades/fin_obra.png',
            '🪧': 'assets/images/icons/oportunidades/fin_obra.png',
            '💵': 'assets/images/icons/oportunidades/venta.png',
            '💸': 'assets/images/icons/oportunidades/venta.png'
          };

          // Validar y mapear icono correspondiente
          let mappedIcon = event.icon;
          if (mappedIcon && iconMap[mappedIcon]) {
            mappedIcon = iconMap[mappedIcon];
          }

          return {
            ...event,
            icon: mappedIcon,
            startDate: event.startDate instanceof Date ? event.startDate : new Date(event.startDate),
            endDate: event.endDate instanceof Date ? event.endDate : new Date(event.endDate),
            createdAt: event.createdAt instanceof Date ? event.createdAt : (event.createdAt ? new Date(event.createdAt) : new Date()),
            updatedAt: event.updatedAt instanceof Date ? event.updatedAt : (event.updatedAt ? new Date(event.updatedAt) : new Date())
          };
        });
        this._loadingFromForm = true;
        this.calendarService.replaceEvents(parsedEvents);
        this._loadingFromForm = false;
      } else {
        this._loadingFromForm = true;
        this.calendarService.replaceEvents([]);
        this._loadingFromForm = false;
      }
    }
  }

  /**
   * Guarda eventos en el FormControl de la propiedad
   */
  private saveEventsToPropertyForm(): void {
    if (!this.propertyForm || this._loadingFromForm) {
      return;
    }

    const calendarEventsControl = this.propertyForm.get('calendarEvents');
    if (calendarEventsControl) {
      const events = this.calendarService.getEventsSnapshot();
      calendarEventsControl.setValue(events, { emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Limpiar listeners globales
    document.removeEventListener('mousemove', this.onGlobalMouseMove.bind(this));
    document.removeEventListener('mouseup', this.onGlobalMouseUp.bind(this));
    document.removeEventListener('touchmove', this.onGlobalTouchMove.bind(this));
    document.removeEventListener('touchend', this.onGlobalTouchEnd.bind(this));
  }

  /**
   * Suscribe a los cambios de estado
   */
  private subscribeToState(): void {
    // Suscribirse a eventos — skip(1) evita que la emisión inicial del BehaviorSubject
    // (estado vacío tras resetState) sobreescriba el FormControl antes de cargar del form.
    this.calendarService.events$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(events => {
        this.events = events;
        this.saveEventsToPropertyForm();
        this.cdr.markForCheck();
      });

    // Suscribirse a evento seleccionado
    this.calendarService.selectedEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedEvent => {
        this.selectedEvent = selectedEvent;
        this.cdr.markForCheck();
      });

    // Suscribirse a estado de carga
    this.calendarService.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => {
        this.isLoading = isLoading;
        this.cdr.markForCheck();
      });

    // Suscribirse a errores
    this.calendarService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        this.error = error;
        this.cdr.markForCheck();
      });

    // Suscribirse a modo de vista
    this.calendarService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.viewMode = state.viewMode;
        this.cdr.markForCheck();
      });
  }

  /**
   * Carga las estadísticas del proyecto
   */
  private loadProjectStats(): void {
    this.calendarService.getProjectStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe(stats => {
        this.projectStats = stats;
        this.cdr.markForCheck();
      });
  }

  /**
   * Cambia el modo de vista
   */
  setViewMode(mode: 'timeline' | 'calendar' | 'list'): void {
    this.viewMode = mode;
    this.calendarService.setViewMode(mode);
  }

  /**
   * Selecciona un evento
   */
  selectEvent(event: CalendarEvent): void {
    this.calendarService.selectEvent(event);
  }

  /**
   * Deselecciona el evento actual
   */
  clearSelection(): void {
    this.calendarService.selectEvent(null);
  }

  /**
   * Aplica filtros de estado
   */
  toggleStatusFilter(status: EventStatus): void {
    if (this.statusFilter.includes(status)) {
      this.statusFilter = this.statusFilter.filter(s => s !== status);
    } else {
      this.statusFilter = [...this.statusFilter, status];
    }
    this.applyFilters();
  }

  /**
   * Aplica filtros de tipo
   */
  toggleTypeFilter(type: EventType): void {
    if (this.typeFilter.includes(type)) {
      this.typeFilter = this.typeFilter.filter(t => t !== type);
    } else {
      this.typeFilter = [...this.typeFilter, type];
    }
    this.applyFilters();
  }

  /**
   * Aplica todos los filtros
   */
  private applyFilters(): void {
    const filters = {
      status: this.statusFilter.length > 0 ? this.statusFilter : undefined,
      type: this.typeFilter.length > 0 ? this.typeFilter : undefined
    };
    this.calendarService.setFilters(filters);
  }

  /**
   * Limpia todos los filtros
   */
  clearFilters(): void {
    this.statusFilter = [];
    this.typeFilter = [];
    this.calendarService.clearFilters();
  }

  /**
   * Obtiene la clase CSS para el estado
   */
  getStatusClass(status: EventStatus): string {
    return `status-${status}`;
  }

  /**
   * Obtiene el color para el estado
   */
  getStatusColor(status: EventStatus): string {
    const statusOption = this.statusOptions.find(option => option.value === status);
    return statusOption?.color || '#6c757d';
  }

  /**
   * Obtiene la etiqueta para el estado
   */
  getStatusLabel(status: EventStatus): string {
    const statusOption = this.statusOptions.find(option => option.value === status);
    return statusOption?.label || 'Desconocido';
  }

  /**
   * Obtiene el icono para el tipo
   */
  getTypeIcon(type: EventType): string {
    const typeOption = this.typeOptions.find(option => option.value === type);
    return typeOption?.icon || '⭐';
  }

  /**
   * Verifica si un filtro está activo
   */
  isFilterActive(filterType: 'status' | 'type', value: string): boolean {
    if (filterType === 'status') {
      return this.statusFilter.includes(value as EventStatus);
    } else {
      return this.typeFilter.includes(value as EventType);
    }
  }

  /**
   * Obtiene eventos filtrados
   */
  get filteredEvents(): CalendarEvent[] {
    return this.events.filter(event => {
      const statusMatch = this.statusFilter.length === 0 || this.statusFilter.includes(event.status);
      const typeMatch = this.typeFilter.length === 0 || this.typeFilter.includes(event.type);
      return statusMatch && typeMatch;
    });
  }

  /**
   * Obtiene el progreso del proyecto
   */
  get projectProgress(): number {
    return this.projectStats.completionPercentage;
  }

  /**
   * Obtiene el estado del progreso
   */
  get progressStatus(): string {
    const percentage = this.projectProgress;
    if (percentage === 0) return 'No iniciado';
    if (percentage < 25) return 'Iniciando';
    if (percentage < 50) return 'En progreso';
    if (percentage < 75) return 'Avanzado';
    if (percentage < 100) return 'Casi terminado';
    return 'Completado';
  }

  /**
   * Abre el formulario de eventos
   */
  openEventForm(event?: CalendarEvent): void {
    this.editingEvent = event || null;
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (event) {
      // Modo edición
      this.newEvent = {
        title: event.title,
        description: event.description,
        startDate: event.startDate,
        endDate: event.endDate,
        icon: event.icon || '📅'
      };
      this.startDateString = this.formatDateForInput(event.startDate);
      this.endDateString = this.formatDateForInput(event.endDate);
    } else {
      // Modo creación
      this.newEvent = {
        title: '',
        description: '',
        startDate: today,
        endDate: tomorrow,
        icon: '📅'
      };
      this.startDateString = this.formatDateForInput(today);
      this.endDateString = this.formatDateForInput(tomorrow);
    }
    
    this.showEventModal = true;
  }

  /**
   * Selecciona un icono
   */
  selectIcon(icon: string): void {
    this.newEvent.icon = icon;
  }

  /**
   * Track by function para ngFor
   */
  trackByEventId(index: number, event: CalendarEvent): string {
    return event.id;
  }

  /**
   * Actualiza la fecha de un evento
   */
  updateEventDate(event: CalendarEvent, field: 'startDate' | 'endDate', changeEvent: Event): void {
    const target = changeEvent.target as HTMLInputElement;
    const newDate = new Date(target.value);
    
    if (field === 'startDate') {
      this.calendarService.updateEvent(event.id, { startDate: newDate });
    } else {
      this.calendarService.updateEvent(event.id, { endDate: newDate });
    }
  }


  /**
   * Elimina un evento
   */
  deleteEvent(event: CalendarEvent): void {
    // Usar un modal personalizado en lugar de confirm()
    this.showDeleteConfirmation(event).then((confirmed) => {
      if (confirmed) {
        this.calendarService.deleteEvent(event.id).subscribe({
          next: () => {
            this.saveEventsToPropertyForm();
            setTimeout(() => {
              this.cdr.detectChanges();
            }, 0);
          },
          error: (error) => {
            console.error('❌ ERROR DELETING EVENT:', error);
            alert('Error al eliminar el evento. Inténtalo de nuevo.');
          }
        });
      }
    });
  }

  /**
   * Muestra confirmación personalizada para eliminar
   */
  private showDeleteConfirmation(event: CalendarEvent): Promise<boolean> {
    const message = `¿Estás seguro de que quieres eliminar el evento "${event.title}"?`;
    
    // Crear modal personalizado
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999999;
      font-family: 'Montserrat', sans-serif;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      text-align: center;
    `;

    const title = document.createElement('h3');
    title.textContent = 'Confirmar Eliminación';
    title.style.cssText = `
      margin: 0 0 16px 0;
      color: #333;
      font-size: 18px;
      font-weight: 600;
    `;

    const messageEl = document.createElement('p');
    messageEl.textContent = message;
    messageEl.style.cssText = `
      margin: 0 0 24px 0;
      color: #666;
      font-size: 14px;
      line-height: 1.5;
    `;

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: center;
    `;

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancelar';
    cancelBtn.style.cssText = `
      background: #f8f9fa;
      color: #666;
      border: 1px solid #ddd;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
    `;

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = 'Eliminar';
    confirmBtn.style.cssText = `
      background: #dc3545;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
    `;

    // Los event listeners se manejan en el Promise

    // Construir modal
    buttonContainer.appendChild(cancelBtn);
    buttonContainer.appendChild(confirmBtn);
    modalContent.appendChild(title);
    modalContent.appendChild(messageEl);
    modalContent.appendChild(buttonContainer);
    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Retornar promesa que se resuelve cuando se hace clic
    return new Promise<boolean>((resolve) => {
      cancelBtn.addEventListener('click', () => {
        document.body.removeChild(modal);
        resolve(false);
      });
      
      confirmBtn.addEventListener('click', () => {
        document.body.removeChild(modal);
        resolve(true);
      });

      // Cerrar al hacer clic fuera del modal
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          document.body.removeChild(modal);
          resolve(false);
        }
      });
    });
  }

  /**
   * Reintenta la operación en caso de error
   */
  retry(): void {
    this.error = null;
    this.calendarService.retry();
  }

  /**
   * Actualiza el progreso de un evento
   */
  updateEventProgress(event: CalendarEvent, changeEvent: Event): void {
    const target = changeEvent.target as HTMLInputElement;
    const progress = Math.min(100, Math.max(0, parseInt(target.value) || 0));
    this.calendarService.updateEvent(event.id, { progress });
  }

  /**
   * Actualiza la descripción de un evento
   */
  updateEventDescription(event: CalendarEvent, changeEvent: Event): void {
    const target = changeEvent.target as HTMLTextAreaElement;
    const description = target.value.trim();
    this.calendarService.updateEvent(event.id, { description });
  }

  /**
   * Cierra el modal de eventos
   */
  closeEventModal(event?: Event): void {
    if (event && event.target !== event.currentTarget) return;
    this.showEventModal = false;
    this.editingEvent = null;
  }


  /**
   * Formatea una fecha para el input de tipo date
   */
  formatDateForInput(date: Date | string | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }

  /**
   * Guarda el evento
   */
  saveEvent(): void {
    // Validaciones requeridas
    if (!this.newEvent.title?.trim()) {
      alert('Por favor, ingresa un título para el evento');
      return;
    }

    if (!this.newEvent.description?.trim()) {
      alert('Por favor, ingresa una descripción para el evento');
      return;
    }

    if (!this.startDateString) {
      alert('Por favor, selecciona una fecha de inicio');
      return;
    }

    if (!this.endDateString) {
      alert('Por favor, selecciona una fecha de fin');
      return;
    }

    // Validar que la fecha de fin sea posterior a la de inicio
    if (new Date(this.startDateString) >= new Date(this.endDateString)) {
      alert('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    if (!this.newEvent.icon?.trim()) {
      alert('Por favor, selecciona un icono para el evento');
      return;
    }

    const eventData: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'> = {
      title: this.newEvent.title.trim(),
      description: this.newEvent.description.trim(),
      startDate: new Date(this.startDateString),
      endDate: new Date(this.endDateString),
      icon: this.newEvent.icon.trim(),
      status: 'pending' as EventStatus,
      type: 'custom' as EventType,
      color: '#6c757d',
      isEditable: true,
      order: this.events.length
    };

    if (this.editingEvent) {
      this.calendarService.updateEvent(this.editingEvent.id, eventData).subscribe({
        next: () => {
          this.saveEventsToPropertyForm();
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 0);
        },
        error: (error) => {
          console.error('❌ ERROR UPDATING EVENT:', error);
          alert('Error al actualizar el evento. Inténtalo de nuevo.');
        }
      });
    } else {
      this.calendarService.createEvent(eventData).subscribe({
        next: () => {
          this.saveEventsToPropertyForm();
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 0);
        },
        error: (error) => {
          console.error('❌ ERROR CREATING EVENT:', error);
          alert('Error al crear el evento. Inténtalo de nuevo.');
        }
      });
    }

    this.closeEventModal();
  }

  /**
   * Edita un evento existente
   */
  editEvent(event: CalendarEvent): void {
    this.openEventForm(event);
  }

  /**
   * Configura los listeners globales para drag & drop
   */
  setupGlobalDragListeners(): void {
    document.addEventListener('mousemove', this.onGlobalMouseMove.bind(this));
    document.addEventListener('mouseup', this.onGlobalMouseUp.bind(this));
    document.addEventListener('touchmove', this.onGlobalTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.onGlobalTouchEnd.bind(this), { passive: false });
  }

  /**
   * Inicia el drag & drop
   */
  startDrag(event: MouseEvent | TouchEvent, calendarEvent: CalendarEvent): void {
    console.log('🚀 START DRAG:', calendarEvent.title, 'Progress:', calendarEvent.progress);
    
    event.preventDefault();
    event.stopPropagation();
    
    this.isDragging = true;
    this.currentDraggingEvent = calendarEvent;
    
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    this.dragStartX = clientX;
    this.dragStartProgress = calendarEvent.progress || 0;
    
    // Agregar clase de dragging
    const target = event.target as HTMLElement;
    const titleBar = target.closest('.event-title-bar') as HTMLElement;
    if (titleBar) {
      titleBar.classList.add('dragging');
      console.log('✅ Added dragging class');
    }
  }

  /**
   * Maneja el mouse move global
   */
  onGlobalMouseMove(event: MouseEvent): void {
    if (!this.isDragging || !this.currentDraggingEvent) return;
    
    console.log('🖱️ MOUSE MOVE:', event.clientX);
    event.preventDefault();
    this.handleDrag(event.clientX);
  }

  /**
   * Maneja el mouse up global
   */
  onGlobalMouseUp(event: MouseEvent): void {
    if (!this.isDragging || !this.currentDraggingEvent) return;
    
    this.endDrag();
  }

  /**
   * Maneja el touch move global
   */
  onGlobalTouchMove(event: TouchEvent): void {
    if (!this.isDragging || !this.currentDraggingEvent) return;
    
    event.preventDefault();
    this.handleDrag(event.touches[0].clientX);
  }

  /**
   * Maneja el touch end global
   */
  onGlobalTouchEnd(event: TouchEvent): void {
    if (!this.isDragging || !this.currentDraggingEvent) return;
    
    this.endDrag();
  }

  /**
   * Maneja el drag
   */
  handleDrag(clientX: number): void {
    if (!this.currentDraggingEvent) {
      console.log('❌ No current dragging event');
      return;
    }
    
    // Buscar la barra de progreso del evento actual
    const titleBars = document.querySelectorAll('.event-title-bar');
    let targetTitleBar: HTMLElement | null = null;
    
    for (let i = 0; i < titleBars.length; i++) {
      const titleBar = titleBars[i] as HTMLElement;
      if (titleBar.classList.contains('dragging')) {
        targetTitleBar = titleBar;
        break;
      }
    }
    
    if (!targetTitleBar) {
      console.log('❌ No target title bar found');
      return;
    }
    
    const rect = targetTitleBar.getBoundingClientRect();
    
    // Calcular el porcentaje basado en la posición del mouse
    const relativeX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (relativeX / rect.width) * 100));
    
    console.log('📊 DRAG CALC:', {
      clientX,
      rectLeft: rect.left,
      rectWidth: rect.width,
      relativeX,
      percentage: Math.round(percentage),
      eventTitle: this.currentDraggingEvent.title
    });
    
    // Actualizar el progreso en tiempo real
    const newProgress = Math.round(percentage);
    
    // Actualizar directamente en el array para feedback inmediato
    const eventIndex = this.events.findIndex(e => e.id === this.currentDraggingEvent!.id);
    if (eventIndex !== -1) {
      this.events[eventIndex].progress = newProgress;
      this.events[eventIndex].updatedAt = new Date();
      console.log('🎯 UI UPDATE:', this.events[eventIndex].title, 'Progress:', this.events[eventIndex].progress);
    }
    
    // También actualizar en el servicio para persistencia
    this.calendarService.updateEvent(this.currentDraggingEvent.id, { progress: newProgress });
    
    // Forzar la detección de cambios para actualizar la UI
    this.cdr.detectChanges();
  }

  /**
   * Termina el drag & drop
   */
  endDrag(): void {
    if (!this.isDragging) return;
    
    console.log('🏁 END DRAG');
    this.isDragging = false;
    this.currentDraggingEvent = null;
    
    // Remover clase de dragging de todas las barras
    const titleBars = document.querySelectorAll('.event-title-bar.dragging');
    titleBars.forEach(bar => bar.classList.remove('dragging'));
  }

}